import { useEffect, useMemo, useRef, useState } from "react";

type SheetState = "closed" | "partial" | "full";
type Origin = "handle" | "content";

type Params = {
  enabled: boolean;
  fullY: () => number;
  partialY: () => number;
  closedY: () => number;
  contentRef: React.RefObject<HTMLElement>;
  onClosed?: () => void; 
  lockGestures?: boolean;
};

export function useBottomSheet3States({
  enabled,
  fullY,
  partialY,
  closedY,
  contentRef,
  onClosed,
  lockGestures=false,
}: Params) {
  const sheetRef = useRef<HTMLDivElement | null>(null);

    const lockRef = useRef(lockGestures);
    useEffect(() => {
      lockRef.current = lockGestures;
    }, [lockGestures]);

const touchContentActive = useRef(false);
const touchContentStartState = useRef<SheetState>("closed");
const touchContentStartY = useRef(0);
const touchContentLastY = useRef(0);
const touchContentStartTime = useRef(0);
const touchContentBase = useRef(0);


  const startY = useRef(0);
  const lastY = useRef(0);
  const startTime = useRef(0);
  const dragStartBase = useRef(0);

  const pointerIdRef = useRef<number | null>(null);
  const capturedRef = useRef(false);

  const startState = useRef<SheetState>("closed");
  const origin = useRef<Origin>("content");
  const dragging = useRef(false);

  const [state, setState] = useState<SheetState>("closed");
  const [dragY, setDragY] = useState<number | null>(null);

  const points = useMemo(() => {
    const FULL = fullY();
    const PARTIAL = partialY();
    const CLOSED = closedY();
    return { FULL, PARTIAL, CLOSED, MIN: FULL, MAX: CLOSED };
  }, [fullY, partialY, closedY]);

  const currentTranslateY = () => {
    if (dragY !== null) return dragY;
    if (state === "full") return points.FULL;
    if (state === "partial") return points.PARTIAL;
    return points.CLOSED;
  };

  const snapTo = (next: SheetState) => {
    setState(next);
    setDragY(null);
    if (next === "closed") {
        window.setTimeout(() => {
        onClosed?.();
        }, 350);
    }
  };

  const setOverflowWhileDragging = (locked: boolean) => {
    const el = contentRef.current;
    if (!el) return;
    el.style.overflowY = locked ? "hidden" : "auto";
  };

  const isHandleTarget = (target: EventTarget | null) => {
    const t = target as HTMLElement | null;
    if (!t) return false;
    return !!t.closest(".topBarBorder, .closeFilterOnglet, .borderFilterOnglet");
  };

  const tryCapture = (el: HTMLElement, pointerId: number) => {
    if (capturedRef.current) return;
    try {
      el.setPointerCapture(pointerId);
      capturedRef.current = true;
    } catch {
    }
  };

  const releaseCapture = () => {
    const el = sheetRef.current;
    const pid = pointerIdRef.current;
    if (!el || pid == null) return;
    if (!capturedRef.current) return;
    try {
      el.releasePointerCapture(pid);
    } catch {
    }
    capturedRef.current = false;
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!enabled) return;
    if (lockRef.current) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;

    pointerIdRef.current = e.pointerId;
    capturedRef.current = false;

    origin.current = isHandleTarget(e.target) ? "handle" : "content";
    startState.current = state;
    dragging.current = false;

    startY.current = e.clientY;
    lastY.current = e.clientY;
    startTime.current = performance.now();
    dragStartBase.current = currentTranslateY();

    if (origin.current === "handle") {
      tryCapture(e.currentTarget as HTMLElement, e.pointerId);
      setOverflowWhileDragging(true);
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!enabled) return;
    if (lockRef.current) return;
    lastY.current = e.clientY;
    const delta = lastY.current - startY.current;

    const content = contentRef.current;
    const atTop = content ? content.scrollTop <= 2 : true;

    const START_DRAG_THRESHOLD = 8;

    if (origin.current === "content") {
      if (delta < 0) return;
      if (!atTop) return;
      if (!dragging.current && delta < START_DRAG_THRESHOLD) return;
      dragging.current = true;
      tryCapture(e.currentTarget as HTMLElement, e.pointerId);

      if (e.cancelable) e.preventDefault();
      setOverflowWhileDragging(true);

      const base = dragStartBase.current + delta;
      const min = startState.current === "full" ? points.FULL : points.PARTIAL;
      const max = startState.current === "full" ? points.PARTIAL : points.CLOSED;

      setDragY(Math.max(min, Math.min(base, max)));
      return;
    }

    if (!dragging.current && Math.abs(delta) < START_DRAG_THRESHOLD) return;
    dragging.current = true;

    if (e.cancelable) e.preventDefault();

    const next = dragStartBase.current + delta;
    setDragY(Math.max(points.MIN, Math.min(next, points.MAX)));
  };

  const onPointerUp = () => {
    if (!enabled) return;
    if (lockRef.current) {
      touchContentActive.current = false;
      dragging.current = false;
      setDragY(null);
      setOverflowWhileDragging(false);
      releaseCapture();
      pointerIdRef.current = null;
      return;
    }
    finalizeSnap();
  };

  const finalizeSnap = () => {
    const dt = performance.now() - startTime.current;
    const delta = lastY.current - startY.current;

    const FAST_TIME = 260;
    const FAST_DIST = 55;

    if (!dragging.current || dragY === null) {
      if (origin.current === "content" && delta > FAST_DIST && dt < 500) {
        if (startState.current === "full") snapTo("partial");
        else if (startState.current === "partial") snapTo("closed");
      }

      setOverflowWhileDragging(false);
      setDragY(null);
      releaseCapture();
      pointerIdRef.current = null;
      dragging.current = false;
      return;
    }

    if (dt < FAST_TIME && Math.abs(delta) > FAST_DIST) {
      if (delta > 0) {
        if (origin.current === "content") {
          if (startState.current === "full") snapTo("partial");
          else snapTo("closed");
        } else {
          if (startState.current === "full") snapTo("partial");
          else snapTo("closed");
        }
      } else {
        if (origin.current === "handle") snapTo("full");
        else snapTo(startState.current);
      }

      setOverflowWhileDragging(false);
      setDragY(null);
      releaseCapture();
      pointerIdRef.current = null;
      dragging.current = false;
      return;
    }
    if (origin.current === "content") {
      if (delta > 0) {
        if (startState.current === "full") snapTo("partial");
        else snapTo("closed");
      } else {
        snapTo(startState.current);
      }

      setOverflowWhileDragging(false);
      setDragY(null);
      releaseCapture();
      pointerIdRef.current = null;
      dragging.current = false;
      return;
    }
    const y = dragY;
    const dFull = Math.abs(y - points.FULL);
    const dPartial = Math.abs(y - points.PARTIAL);
    const dClosed = Math.abs(y - points.CLOSED);

    const m = Math.min(dFull, dPartial, dClosed);
    if (m === dFull) snapTo("full");
    else if (m === dPartial) snapTo("partial");
    else snapTo("closed");

    setOverflowWhileDragging(false);
    setDragY(null);
    releaseCapture();
    pointerIdRef.current = null;
    dragging.current = false;
  };

  const style: React.CSSProperties = enabled
    ? {
        transform: `translateY(${currentTranslateY()}px)`,
        transition: dragY === null ? "transform 0.35s ease" : "none",
        willChange: "transform",
      }
    : {};

  useEffect(() => {
    if (!enabled) return;
    setDragY(null);
  }, [enabled]);
  useEffect(() => {
    if (!enabled) return;
    const el = contentRef.current;
    if (!el) return;
    el.style.paddingBottom = state === "partial" ? "30em" : "";
    return () => {
      el.style.paddingBottom = "";
    };
  }, [enabled, state, contentRef]);


  useEffect(() => {
  if (!enabled) return;

  const content = contentRef.current as HTMLElement | null;
  if (!content) return;

  const FAST_TIME = 260;
  const FAST_DIST = 70;
  const START_DRAG_THRESHOLD = 10;

  const onStart = (ev: TouchEvent) => {
    if (lockRef.current) return;
    if (!ev.touches || ev.touches.length === 0) return;

    touchContentActive.current = true;
    touchContentStartState.current = state;
    const y = ev.touches[0].clientY;

    touchContentStartY.current = y;
    touchContentLastY.current = y;
    touchContentStartTime.current = performance.now();
    touchContentBase.current = currentTranslateY();
  };

  const onMove = (ev: TouchEvent) => {
    if (lockRef.current) return;
    if (!touchContentActive.current) return;
    if (!ev.touches || ev.touches.length === 0) return;

    const y = ev.touches[0].clientY;
    touchContentLastY.current = y;

    const delta = y - touchContentStartY.current; 
    if (delta < 0) return;

    const atTop = content.scrollTop <= 2;
    if (!atTop) return;

    if (delta < START_DRAG_THRESHOLD) return;

    if (ev.cancelable) ev.preventDefault();
    setOverflowWhileDragging(true);

    const base = touchContentBase.current + delta;
    const startS = touchContentStartState.current;
    const min = startS === "full" ? points.FULL : points.PARTIAL;
    const max = startS === "full" ? points.PARTIAL : points.CLOSED;

    setDragY(Math.max(min, Math.min(base, max)));
  };

  const onEnd = () => {
    if (lockRef.current) return;
    if (!touchContentActive.current) return;
    touchContentActive.current = false;

    const dt = performance.now() - touchContentStartTime.current;
    const delta = touchContentLastY.current - touchContentStartY.current;

    if (dragY === null) {
      setOverflowWhileDragging(false);
      return;
    }

    if (dt < FAST_TIME && delta > FAST_DIST) {
      if (touchContentStartState.current === "full") {
        snapTo("closed");
      } else {
        snapTo("closed");
      }
      setOverflowWhileDragging(false);
      return;
    }

    if (touchContentStartState.current === "full") snapTo("partial");
    else snapTo("closed");

    setOverflowWhileDragging(false);
  };

  content.addEventListener("touchstart", onStart, { passive: true });
  content.addEventListener("touchmove", onMove, { passive: false });
  content.addEventListener("touchend", onEnd, { passive: true });
  content.addEventListener("touchcancel", onEnd, { passive: true });

  return () => {
    content.removeEventListener("touchstart", onStart);
    content.removeEventListener("touchmove", onMove);
    content.removeEventListener("touchend", onEnd);
    content.removeEventListener("touchcancel", onEnd);
  };
}, [enabled, state, dragY, points.FULL, points.PARTIAL, points.CLOSED]);


  return {
    sheetRef,
    state,
    setState: snapTo,
    style,
    bind: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
    },
  };
}
